import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyCaseyReasMode({ brand }: ModeRendererProps) {
  // Processing lineage: generative systems as brand fabric
  // Procedural textures, systems diagrams, loops
  
  // Seeded random for reproducibility
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.deep} />
      
      {/* Processing-style system of agents/particles */}
      {Array.from({ length: 80 }).map((_, i) => {
        const seed = i * 123.456;
        const x = 80 + seededRandom(seed) * 640;
        const y = 80 + seededRandom(seed * 1.5) * 300;
        const r = 2 + seededRandom(seed * 2) * 8;
        const colors = [brand.aqua, brand.violet, brand.amber, brand.white];
        const color = colors[Math.floor(seededRandom(seed * 3) * 4)];
        
        return (
          <circle
            key={`agent-${i}`}
            cx={x}
            cy={y}
            r={r}
            fill={color}
            opacity={0.6}
          />
        );
      })}
      
      {/* Connection lines between nearby agents */}
      {Array.from({ length: 60 }).map((_, i) => {
        const seed = i * 234.567;
        const x1 = 80 + seededRandom(seed) * 640;
        const y1 = 80 + seededRandom(seed * 1.5) * 300;
        const x2 = 80 + seededRandom(seed * 2) * 640;
        const y2 = 80 + seededRandom(seed * 2.5) * 300;
        
        // Only draw line if points are reasonably close
        const dist = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        if (dist < 150) {
          return (
            <line
              key={`connection-${i}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={brand.aqua}
              strokeWidth="0.5"
              opacity={0.2}
            />
          );
        }
        return null;
      })}
      
      {/* Procedural loop pattern - Vasarely influence */}
      {Array.from({ length: 15 }).map((_, i) => {
        const cx = 397;
        const cy = 520;
        const r = 30 + i * 15;
        const segments = 16;
        
        return (
          <g key={`loop-${i}`}>
            {Array.from({ length: segments }).map((_, j) => {
              const angle1 = (j / segments) * Math.PI * 2;
              const angle2 = ((j + 1) / segments) * Math.PI * 2;
              const modulation = Math.sin(i * 0.3 + j * 0.5) * 5;
              
              return (
                <line
                  key={`segment-${j}`}
                  x1={cx + Math.cos(angle1) * (r + modulation)}
                  y1={cy + Math.sin(angle1) * (r + modulation)}
                  x2={cx + Math.cos(angle2) * (r + modulation)}
                  y2={cy + Math.sin(angle2) * (r + modulation)}
                  stroke={j % 3 === 0 ? brand.violet : j % 3 === 1 ? brand.aqua : brand.amber}
                  strokeWidth="2"
                  opacity={0.4}
                />
              );
            })}
          </g>
        );
      })}
      
      {/* Grid of generative cells */}
      {Array.from({ length: 6 }).map((_, row) =>
        Array.from({ length: 9 }).map((_, col) => {
          const x = 80 + col * 72;
          const y = 760 + row * 55;
          const seed = row * 9 + col;
          const pattern = Math.floor(seededRandom(seed * 100) * 4);
          
          if (pattern === 0) {
            // Horizontal lines
            return (
              <g key={`cell-${row}-${col}`}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <line
                    key={`h-${i}`}
                    x1={x + 5}
                    y1={y + 5 + i * 7}
                    x2={x + 60}
                    y2={y + 5 + i * 7}
                    stroke={brand.aqua}
                    strokeWidth="1.5"
                    opacity={0.6}
                  />
                ))}
              </g>
            );
          } else if (pattern === 1) {
            // Circles
            return (
              <g key={`cell-${row}-${col}`}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <circle
                    key={`c-${i}`}
                    cx={x + 32}
                    cy={y + 27}
                    r={8 + i * 8}
                    fill="none"
                    stroke={brand.violet}
                    strokeWidth="1.5"
                    opacity={0.5}
                  />
                ))}
              </g>
            );
          } else if (pattern === 2) {
            // Diagonal grid
            return (
              <g key={`cell-${row}-${col}`}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <line
                    key={`d-${i}`}
                    x1={x + i * 8}
                    y1={y + 5}
                    x2={x + 60}
                    y2={y + 50 - i * 6}
                    stroke={brand.amber}
                    strokeWidth="1"
                    opacity={0.5}
                  />
                ))}
              </g>
            );
          } else {
            // Rectangles
            return (
              <g key={`cell-${row}-${col}`}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <rect
                    key={`r-${i}`}
                    x={x + 10 + i * 14}
                    y={y + 10 + i * 10}
                    width={30 - i * 8}
                    height={30 - i * 8}
                    fill="none"
                    stroke={brand.white}
                    strokeWidth="1.5"
                    opacity={0.5}
                  />
                ))}
              </g>
            );
          }
        })
      )}
      
      {/* Flowing lines - system flow diagram */}
      {Array.from({ length: 12 }).map((_, i) => {
        const y = 420 + i * 8;
        const amplitude = 40 + i * 3;
        const frequency = 0.02;
        
        const path = Array.from({ length: 50 }).map((_, j) => {
          const x = 80 + j * 13;
          const py = y + Math.sin(j * frequency + i * 0.3) * amplitude;
          return `${j === 0 ? "M" : "L"} ${x},${py}`;
        }).join(" ");
        
        return (
          <path
            key={`flow-${i}`}
            d={path}
            fill="none"
            stroke={brand.violet}
            strokeWidth="1"
            opacity={0.2}
          />
        );
      })}
    </g>
  );
}
